import React, { useState } from "react";
import './App.css';
import { getCode } from 'country-list';
import styled from '@emotion/styled';
import Product from "./components/product";
import Filter from "./components/filter";
import { colors, mediaQueries } from "./variables";
import ReactCountryFlag from "react-country-flag";

function App() {
  const [productsData, setProductsData] = useState([]);
  const [listView, setListView] = useState(true);
  const [memberTier, setMemberTier] = useState([]);
  const [globalProcessing, setGlobalProcessing] = useState([]);

  // 顯示方式切換（filter 的 list/grid toggle）：
  //   list（預設）→ 試算表(V1)、grid → 緊湊卡(V2)
  const currentVersion = listView ? 1 : 2;

  const sortedProducts = productsData?.sort((a, b) => {
    if (a.nation === '衣索比亞' && b.nation !== '衣索比亞') return -1;
    if (a.nation !== '衣索比亞' && b.nation === '衣索比亞') return 1;
    return 0;
  });

  const processingTitles = globalProcessing.map(process => process.title);

  const groupedProducts = sortedProducts?.reduce((acc, product) => {
    const nation = product.nation?.name_translations['zh-hant'] || 'Unknown';
    const processingMethod = product.processing || 'Unknown';
    if (!acc[nation]) acc[nation] = {};
    if (!acc[nation][processingMethod]) acc[nation][processingMethod] = [];
    acc[nation][processingMethod].push(product);
    return acc;
  }, {});

  const finalProductsArray = Object.entries(groupedProducts || {}).flatMap(([, processingGroups]) => {
    const sortedMethods = Object.keys(processingGroups).sort((a, b) =>
      processingTitles.indexOf(a) - processingTitles.indexOf(b)
    );
    return sortedMethods.flatMap(method => processingGroups[method]);
  });

  // 計算各產區豆數（用於分組標題）
  const countByNation = finalProductsArray.reduce((acc, product) => {
    const nation_name = product.nation?.name_translations?.['zh-hant'] || 'Unknown';
    acc[nation_name] = (acc[nation_name] || 0) + 1;
    return acc;
  }, {});

  // ── 分組標題（三版統一用緊湊深綠色條） ─────────────────────
  const renderNationHeader = (nation, index) => {
    const nation_zh = nation?.name_translations?.['zh-hant'] || 'Unknown';
    const nation_en = nation?.name_translations?.en || 'Unknown';
    const count = countByNation[nation_zh] || 0;
    const flag = (
      <ReactCountryFlag
        countryCode={getCode(nation_en || 'Unknown')}
        svg
        style={{ marginRight: '6px' }}
      />
    );
    return (
      <NationBar key={`nation-${index}`} className="full-width">
        <span className="left">{flag}{nation_zh}</span>
        <span className="count">共 {count} 支</span>
      </NationBar>
    );
  };

  const renderProcessingHeader = (processing, index) => (
    <ProcessingBar key={`proc-${index}`} className="full-width">
      {processing}
    </ProcessingBar>
  );

  const renderProducts = () => {
    let currentNation = null;
    let currentProcessing = null;

    return finalProductsArray.map((product, index) => {
      const nation = product.nation || 'Unknown';
      const processing = product.processing || 'Unknown';

      const showNation =
        currentNation?.name_translations?.['zh-hant'] !== nation.name_translations?.['zh-hant'];
      const showProcessing = currentProcessing !== processing;

      if (showNation) currentNation = nation;
      if (showNation || showProcessing) currentProcessing = processing;

      return (
        <React.Fragment key={product.id}>
          {showNation && renderNationHeader(nation, index)}
          {showProcessing && renderProcessingHeader(processing, index)}
          <StyledProduct version={currentVersion}>
            <Product
              props={product}
              version={currentVersion}
              beanIndex={index}
              imageUrl={product.medias[0].images.original.url}
              titleZh={product.title_translations['zh-hant']}
              titleEng={product.title_translations['en']}
              processing={processing !== 'Unknown' ? processing : null}
              memberTier={memberTier}
              listView={listView}
              product_price_tiers={product.product_price_tiers}
              desc={product.seo_description_translations['zh-hant']}
            />
          </StyledProduct>
        </React.Fragment>
      );
    });
  };

  return (
    <div className="App">
      <Filter
        setListView={setListView}
        setProductsData={setProductsData}
        setGlobalMemberTier={setMemberTier}
        setGlobalProcessing={setGlobalProcessing}
      />

      <ProductList className="js-product-list" version={currentVersion}>
        {renderProducts()}
      </ProductList>
    </div>
  );
}

export default App;

// ─── Styled Components ───────────────────────────────────────────────────────

const fullWidth = `grid-column: 1 / -1;`;

// 三版統一的產區 bar
const NationBar = styled.div`
  ${fullWidth}
  box-sizing: border-box; /* padding 不外撐，避免 grid 內 full-width 溢出 */
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${colors.primary};
  color: ${colors.white};
  font-weight: 700;
  font-size: 14px;
  padding: 7px 14px;
  margin-top: 8px;
  .left { display: flex; align-items: center; }
  .count { font-size: 14px; font-weight: 500; opacity: 0.85; }

  ${mediaQueries.mobile} {
    font-size: 14px;
    padding: 6px 10px;
    .count { font-size: 14px; }
  }
`;

const ProcessingBar = styled.div`
  ${fullWidth}
  box-sizing: border-box; /* 同上，防溢出 */
  min-width: 0;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: ${colors.primary};
  border-left: 3px solid ${colors.cta};
  padding: 3px 8px;
  background: #f0f4f0;
`;

// StyledProduct：V1/V3 占整行；V2 為格子
const StyledProduct = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  grid-column: ${props => (props.version === 2 ? 'span 1' : '1 / -1')};
`;

// ProductList grid：V2 多欄卡，其他單列
const ProductList = styled.div`
  display: grid;
  grid-template-columns: ${props => {
    if (props.version === 2) return 'repeat(4, minmax(0, 1fr))';
    return '1fr';
  }};
  gap: ${props => (props.version === 2 ? '10px' : '0')};
  /* V2 卡片網格：同列卡片拉伸到等高（下單釘底），不再高低不齊 */
  align-items: ${props => (props.version === 2 ? 'stretch' : 'start')};

  ${mediaQueries.tablet} {
    grid-template-columns: ${props => (props.version === 2 ? 'repeat(3, minmax(0, 1fr))' : '1fr')};
  }

  ${mediaQueries.mobile} {
    /* 手機：V2 一排兩張卡（與 V1 單欄清單區隔）；其餘版本單欄 */
    grid-template-columns: ${props => (props.version === 2 ? 'repeat(2, minmax(0, 1fr))' : '1fr')};
    gap: ${props => (props.version === 2 ? '8px' : '0')};
  }
`;
